// --- src/components/SuspendedCarts.tsx ---
import React, { useState, useEffect } from 'react';
import { Pause, RotateCcw, Trash2, X, Loader2, Clock, Package, DollarSign } from 'lucide-react';
import { supabaseService } from '../services/supabaseService';
import { toast } from 'react-hot-toast';
import { getUserFriendlyError } from '../utils/errorHelper';

interface SuspendedCart {
  id: string;
  cart_items: Array<{ ean: string; name: string; price: number; quantity: number }>;
  total_amount: number;
  total_items: number;
  notes?: string;
  created_at: string;
  expires_at: string;
}

interface SuspendedCartsProps {
  onClose?: () => void;
  onResume?: (
    cartItems: Array<{ ean: string; name: string; price: number; quantity: number }>
  ) => void;
}

export const SuspendedCarts: React.FC<SuspendedCartsProps> = ({ onClose, onResume }) => {
  const [carts, setCarts] = useState<SuspendedCart[]>([]);
  const [loading, setLoading] = useState(true);
  const [resumingCartId, setResumingCartId] = useState<string | null>(null);
  const [deletingCartId, setDeletingCartId] = useState<string | null>(null);

  useEffect(() => {
    loadCarts();
  }, []);

  const loadCarts = async () => {
    setLoading(true);
    try {
      const fetchedCarts = await supabaseService.getSuspendedCarts();
      setCarts(fetchedCarts);
    } catch (error: any) {
      console.error('Error loading suspended carts:', error);
      toast.error(`Hiba a parkolt kosarak betöltésekor: ${getUserFriendlyError(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async (cartId: string) => {
    if (resumingCartId) return;
    setResumingCartId(cartId);
    try {
      const cartData = await supabaseService.resumeSuspendedCart(cartId);
      toast.success('Kosár betöltve! Folytathatod a vásárlást.');
      if (onResume) {
        onResume(cartData.cart_items);
      }
      await loadCarts(); // Frissítjük a listát
    } catch (error: any) {
      console.error('Error resuming cart:', error);
      toast.error(`Folytatás hiba: ${getUserFriendlyError(error)}`);
    } finally {
      setResumingCartId(null);
    }
  };

  const handleDelete = async (cartId: string) => {
    if (deletingCartId) return;
    if (!confirm('Biztosan törlöd ezt a parkolt kosarat?')) {
      return;
    }
    setDeletingCartId(cartId);
    try {
      await supabaseService.deleteSuspendedCart(cartId);
      toast.success('Parkolt kosár törölve');
      await loadCarts();
    } catch (error: any) {
      console.error('Error deleting cart:', error);
      toast.error(`Törlés hiba: ${getUserFriendlyError(error)}`);
    } finally {
      setDeletingCartId(null);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (diff < 0) {
      return 'Lejárt';
    }
    if (hours > 0) {
      return `${hours} óra ${minutes} perc`;
    }
    return `${minutes} perc`;
  };

  return (
    <div className="p-6 bg-slate-900 text-white min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Pause className="w-6 h-6 text-amber-400" />
            <h1 className="text-2xl font-bold">Parkolt kosarak</h1>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-slate-800 rounded-lg p-4">
            <p className="text-sm text-slate-400 mb-1">Parkolt kosarak</p>
            <p className="text-2xl font-bold">{carts.length}</p>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <p className="text-sm text-slate-400 mb-1">Összes tétel</p>
            <p className="text-2xl font-bold">
              {carts.reduce((sum, cart) => sum + cart.total_items, 0)} db
            </p>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <p className="text-sm text-slate-400 mb-1">Összes érték</p>
            <p className="text-2xl font-bold text-green-400">
              {formatCurrency(carts.reduce((sum, cart) => sum + cart.total_amount, 0))}
            </p>
          </div>
        </div>

        {/* Carts List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-amber-400" />
            <p className="text-slate-400">Betöltés...</p>
          </div>
        ) : carts.length === 0 ? (
          <div className="text-center py-12 bg-slate-800 rounded-lg">
            <Pause className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">Nincsenek parkolt kosarak</p>
            <p className="text-slate-500 text-sm mt-2">
              A parkolt kosarak itt jelennek meg, amikor parkolsz egy kosarat
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {carts.map((cart) => (
              <div
                key={cart.id}
                className="bg-slate-800 rounded-lg p-4 border border-slate-700 hover:border-amber-500 transition"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-400">{formatDate(cart.created_at)}</span>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      new Date(cart.expires_at) < new Date()
                        ? 'bg-red-600/20 text-red-400'
                        : 'bg-amber-600/20 text-amber-400'
                    }`}
                  >
                    {getTimeRemaining(cart.expires_at)}
                  </span>
                </div>

                {/* Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-300">{cart.total_items} tétel</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-400" />
                    <span className="text-lg font-bold text-green-400">
                      {formatCurrency(cart.total_amount)}
                    </span>
                  </div>
                  {cart.notes && (
                    <div className="mt-2 p-2 bg-slate-700 rounded text-sm text-slate-300 italic">
                      "{cart.notes}"
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleResume(cart.id)}
                    disabled={resumingCartId !== null}
                    className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg font-semibold transition flex items-center justify-center gap-2"
                  >
                    {resumingCartId === cart.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Betöltés...
                      </>
                    ) : (
                      <>
                        <RotateCcw className="w-4 h-4" />
                        Folytatás
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(cart.id)}
                    disabled={deletingCartId !== null}
                    className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg transition"
                    title="Törlés"
                  >
                    {deletingCartId === cart.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
